import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { Package, Clock, Factory, Truck, PaperPlaneTilt, CheckCircle, Trophy, FileXls, ChartLineUp, Warning, BellRinging, PencilSimple, Trash, ArrowsClockwise, UserMinus, ClipboardText, CheckCircle as CheckIcon, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import api, { hasRole } from "../lib/api";
import { StatusBadge, AgingBadge } from "../components/badges";
import { useAuth } from "../context/AuthContext";

const currentMonth = () => new Date().toISOString().slice(0, 7);
const CHART_MARGIN = { top: 5, right: 10, left: -10, bottom: 0 };
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 6 };
const AXIS_TICK = { fontSize: 11, fill: "#64748B" };
const LEGEND_STYLE = { fontSize: 12 };
const BAR_RADIUS = [3, 3, 0, 0];
const LINE_DOT = { r: 3 };

const LowStockAlerts = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/inventory/low-stock").then(({ data }) => setItems(data.items)).catch(() => {});
  }, []);

  if (items.length === 0) return null;

  return (
    <div data-testid="low-stock-panel" className="rounded-md border border-amber-300 bg-amber-50">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 p-4">
        <h2 className="flex items-center gap-2 text-base font-bold text-amber-800 md:text-lg">
          <Warning size={18} weight="duotone" /> Low Stock Alerts ({items.length})
        </h2>
        <Link to="/inventory" data-testid="low-stock-inventory-link"
          className="text-xs font-semibold text-amber-700 underline underline-offset-2 hover:text-amber-900">
          Manage inventory →
        </Link>
      </div>
      <div className="flex flex-wrap gap-2 p-4">
        {items.map((it) => (
          <span key={it.id} data-testid={`low-stock-item-${it.name}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-slate-700">
            {it.name}
            <span className="font-mono font-semibold text-red-600">{it.stock}{it.unit ? ` ${it.unit}` : ""}</span>
            <span className="text-slate-400">/ min {it.min_stock}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const PendingApprovals = () => {
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/order-requests").then(({ data }) => setRequests(data.requests)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await api.post(`/order-requests/${r.id}/approve`);
      toast.success(`Approved — order ${data.order_number} created`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(null);
    }
  };

  const reject = async (r) => {
    const reason = window.prompt(`Reject order request from ${r.customer_name}?\nEnter a reason for the customer:`);
    if (!reason || !reason.trim()) return;
    setBusy(r.id);
    try {
      await api.post(`/order-requests/${r.id}/reject`, { reason: reason.trim() });
      toast.success("Request rejected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(null);
    }
  };

  if (requests.length === 0) return null;

  return (
    <div data-testid="pending-approvals-panel" className="rounded-md border border-violet-300 bg-violet-50">
      <div className="border-b border-violet-200 p-4">
        <h2 className="flex items-center gap-2 text-base font-bold text-violet-900 md:text-lg">
          <ClipboardText size={18} weight="duotone" /> Customer Orders Awaiting Approval ({requests.length})
        </h2>
        <p className="mt-0.5 text-xs text-violet-700">Approve to convert into a regular order, or reject with a reason</p>
      </div>
      <ul className="divide-y divide-violet-100">
        {requests.map((r) => (
          <li key={r.id} data-testid={`approval-request-${r.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-900">{r.customer_name} <span className="font-normal text-slate-500">— {r.location}</span></p>
              <p className="mt-0.5 text-xs text-slate-600">
                {r.items.map((i) => `${i.product_name} x${i.quantity}`).join(", ")}
                <span className="ml-1.5 font-mono text-slate-400">(total {r.total_quantity})</span>
              </p>
              {r.notes && <p className="mt-0.5 text-xs text-slate-400">Notes: {r.notes}</p>}
              <p className="mt-0.5 text-xs text-slate-400">{new Date(r.created_at).toLocaleString()} · via {r.customer_account}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid={`approve-request-${r.id}`} onClick={() => approve(r)} disabled={busy === r.id}
                className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors duration-150 hover:bg-emerald-700 disabled:opacity-60">
                <CheckIcon size={13} weight="bold" /> Approve
              </button>
              <button data-testid={`reject-request-${r.id}`} onClick={() => reject(r)} disabled={busy === r.id}
                className="flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors duration-150 hover:bg-red-50 disabled:opacity-60">
                <XCircle size={13} weight="bold" /> Reject
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const InactivityAlerts = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/inactive-sales").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data || data.inactive.length === 0) return null;

  return (
    <div data-testid="inactivity-alerts-panel" className="rounded-md border border-orange-300 bg-orange-50">
      <div className="border-b border-orange-200 p-4">
        <h2 className="flex items-center gap-2 text-base font-bold text-orange-900 md:text-lg">
          <UserMinus size={18} weight="duotone" /> Inactive Salespeople ({data.inactive.length})
        </h2>
        <p className="mt-0.5 text-xs text-orange-700">No orders created in the last {data.days_threshold} days</p>
      </div>
      <ul className="divide-y divide-orange-100">
        {data.inactive.map((p) => (
          <li key={p.name} data-testid={`inactive-sales-${p.name}`} className="flex flex-wrap items-baseline gap-x-2 px-4 py-2.5 text-sm">
            <Link to={`/orders?created_by=${encodeURIComponent(p.name)}`}
              className="font-semibold text-orange-900 underline decoration-orange-300 decoration-dotted underline-offset-4 hover:text-orange-700">
              {p.name}
            </Link>
            {p.days_inactive === null ? (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700">Never created an order</span>
            ) : (
              <span className="text-slate-600">
                {p.days_inactive} days without an order
                <span className="text-slate-400"> · last order {new Date(p.last_order_at).toLocaleDateString()}</span>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const EVENT_ICONS = {
  order_edited: [PencilSimple, "text-sky-700"],
  status_updated: [ArrowsClockwise, "text-indigo-600"],
  order_dispatched: [PaperPlaneTilt, "text-amber-600"],
  order_approved: [CheckIcon, "text-emerald-600"],
  order_rejected: [XCircle, "text-red-600"],
  orders_deleted: [Trash, "text-red-600"],
};

const OrderLink = ({ ev }) => (
  <Link to={`/orders/${ev.order_id}`} className="font-mono font-semibold text-sky-900 hover:underline">{ev.order_number}</Link>
);

const EventText = ({ ev }) => {
  if (ev.type === "order_edited") {
    return (
      <span>
        <OrderLink ev={ev} /> edited by <span className="font-medium">{ev.by}</span>
        <span className="text-slate-500"> (changed: {(ev.changes || []).join(", ")})</span>
      </span>
    );
  }
  if (ev.type === "status_updated") {
    return (
      <span>
        <OrderLink ev={ev} /> status changed by <span className="font-medium">{ev.by}</span>
        <span className="text-slate-500"> ({ev.from_status} → {ev.to_status})</span>
      </span>
    );
  }
  if (ev.type === "order_dispatched") {
    return (
      <span>
        <OrderLink ev={ev} /> dispatched by <span className="font-medium">{ev.by}</span>
        <span className="text-slate-500"> ({ev.dispatched_quantity} units, {ev.item_count} item(s))</span>
      </span>
    );
  }
  if (ev.type === "order_approved") {
    return (
      <span>
        Customer order from <span className="font-medium">{ev.customer_name}</span> approved by <span className="font-medium">{ev.by}</span>
        {" → "}<OrderLink ev={ev} />
      </span>
    );
  }
  if (ev.type === "order_rejected") {
    return (
      <span>
        Customer order from <span className="font-medium">{ev.customer_name}</span> rejected by <span className="font-medium">{ev.by}</span>
        <span className="text-slate-500"> ({ev.reason})</span>
      </span>
    );
  }
  return (
    <span>
      <span className="font-mono font-semibold text-slate-900">{(ev.order_numbers || []).join(", ")}</span>
      {" "}deleted by <span className="font-medium">{ev.by}</span>
      {ev.renumbered > 0 && <span className="text-amber-700"> — {ev.renumbered} order(s) renumbered</span>}
    </span>
  );
};

const RecentChanges = () => {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/activity/recent").then(({ data }) => setEvents(data.events)).catch(() => {});
  }, []);

  if (events.length === 0) return null;

  return (
    <div data-testid="recent-changes-panel" className="rounded-md border border-sky-300 bg-sky-50">
      <div className="border-b border-sky-200 p-4">
        <h2 className="flex items-center gap-2 text-base font-bold text-sky-900 md:text-lg">
          <BellRinging size={18} weight="duotone" /> Recent Changes ({events.length})
        </h2>
        <p className="mt-0.5 text-xs text-sky-700">Edits, status updates, dispatches, deletions and renumbering — latest first</p>
      </div>
      <ul className="divide-y divide-sky-100">
        {events.map((ev) => {
          const [EvIcon, color] = EVENT_ICONS[ev.type] || EVENT_ICONS.orders_deleted;
          return (
            <li key={ev.id} data-testid={`activity-event-${ev.id}`} className="flex flex-wrap items-baseline gap-x-2 px-4 py-2.5 text-sm">
              <EvIcon size={13} weight="duotone" className={`relative top-0.5 shrink-0 ${color}`} />
              <EventText ev={ev} />
              <span className="ml-auto whitespace-nowrap text-xs text-slate-400">{new Date(ev.at).toLocaleString()}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(y, mo - 1).toLocaleString("en", { month: "short" }) + " '" + y.slice(2);
};

const TrendsChart = () => {
  const [trends, setTrends] = useState(null);

  useEffect(() => {
    api.get("/reports/trends").then(({ data }) => setTrends(data.trends)).catch(() => setTrends([]));
  }, []);

  return (
    <div data-testid="monthly-trends" className="rounded-md border border-slate-200 bg-white">
      <div className="border-b border-slate-200 p-4">
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
          <ChartLineUp size={18} weight="duotone" className="text-slate-700" /> Monthly Trends
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">Orders created and total quantity, last 12 months</p>
      </div>
      <div className="p-4">
        {trends === null ? (
          <p className="p-6 text-center text-sm text-slate-400">Loading…</p>
        ) : (
          <div className="h-64 w-full" data-testid="trends-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trends} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={AXIS_TICK} />
                <YAxis yAxisId="left" allowDecimals={false} tick={AXIS_TICK} />
                <YAxis yAxisId="right" orientation="right" tick={AXIS_TICK} />
                <Tooltip labelFormatter={monthLabel} contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                <Bar yAxisId="left" dataKey="orders" name="Orders" fill="#0F172A" radius={BAR_RADIUS} maxBarSize={28} />
                <Line yAxisId="right" dataKey="total_quantity" name="Total Qty" stroke="#D97706" strokeWidth={2} dot={LINE_DOT} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

const Leaderboard = () => {
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState(null);

  useEffect(() => {
    setRows(null);
    api.get("/reports/leaderboard", { params: { month } })
      .then(({ data }) => setRows(data.leaderboard))
      .catch(() => setRows([]));
  }, [month]);

  const exportExcel = async () => {
    try {
      const { data } = await api.get("/reports/leaderboard/excel", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dcn_leaderboard_${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Leaderboard exported to Excel");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="sales-leaderboard" className="rounded-md border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
          <Trophy size={18} weight="duotone" className="text-amber-500" /> Sales Leaderboard
        </h2>
        <div className="flex items-center gap-2">
          <input data-testid="leaderboard-month-input" type="month" value={month} max={currentMonth()}
            onChange={(e) => e.target.value && setMonth(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm outline-none" />
          <button data-testid="leaderboard-export-button" onClick={exportExcel}
            className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100">
            <FileXls size={15} weight="duotone" /> Excel
          </button>
        </div>
      </div>
      {rows === null ? (
        <p className="p-6 text-center text-sm text-slate-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="p-6 text-center text-sm text-slate-400" data-testid="leaderboard-empty">No orders created this month.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">#</th>
                <th className="p-3">Salesperson</th>
                <th className="p-3 text-right">Orders</th>
                <th className="p-3 text-right">Total Qty</th>
                <th className="p-3 text-right">Delivered</th>
              </tr>
            </thead>
            <tbody data-testid="leaderboard-table-body">
              {rows.map((r, i) => (
                <tr key={r.name} className={`border-b border-slate-100 ${r.orders === 0 ? "bg-red-50/60" : ""}`}
                  data-testid={r.orders === 0 ? `leaderboard-zero-row-${r.name}` : undefined}>
                  <td className="p-3 font-mono text-slate-500">
                    {i === 0 && r.orders > 0 ? <Trophy size={16} weight="fill" className="text-amber-500" /> : i + 1}
                  </td>
                  <td className="p-3">
                    <Link to={`/orders?created_by=${encodeURIComponent(r.name)}`}
                      data-testid={`leaderboard-name-link-${r.name}`}
                      title={`View all orders created by ${r.name}`}
                      className="font-medium text-slate-900 underline decoration-slate-300 decoration-dotted underline-offset-4 transition-colors duration-150 hover:text-slate-600 hover:decoration-slate-500">
                      {r.name}
                    </Link>
                    {r.orders === 0 && (
                      <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700">
                        No orders
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right font-mono font-semibold">{r.orders}</td>
                  <td className="p-3 text-right font-mono">{r.total_quantity}</td>
                  <td className="p-3 text-right font-mono">{r.delivered}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, testId, delay }) => (
  <div
    data-testid={testId}
    className="fade-in-up rounded-md border border-slate-200 bg-white p-4 transition-colors duration-150 hover:border-slate-400"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <Icon size={18} weight="duotone" className="text-slate-400" />
    </div>
    <p className="mt-2 text-3xl font-bold text-slate-900">{value}</p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const isCustomer = hasRole(user, "customer");

  useEffect(() => {
    if (isCustomer) return;
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [isCustomer]);

  if (isCustomer) return <Navigate to="/portal" replace />;

  if (!stats)
    return <div className="text-sm text-slate-500">Loading dashboard…</div>;

  const sc = stats.status_counts;
  const ac = stats.aging_counts;

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">Dashboard</h1>
        <p className="mt-1 text-sm text-slate-500">
          Welcome back, {user?.name}. Live overview of all orders.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <StatCard icon={Package} label="Total Orders" value={stats.total_orders} testId="stat-total-orders" delay={0} />
        <StatCard icon={Clock} label="Pending" value={sc.order_received} testId="stat-pending-orders" delay={40} />
        <StatCard icon={Factory} label="In Production" value={sc.in_production} testId="stat-in-production" delay={80} />
        <StatCard icon={Truck} label="Ready to Dispatch" value={sc.ready_to_dispatch} testId="stat-ready-dispatch" delay={120} />
        <StatCard icon={PaperPlaneTilt} label="Dispatched" value={sc.dispatched} testId="stat-dispatched" delay={160} />
        <StatCard icon={CheckCircle} label="Delivered" value={sc.delivered} testId="stat-delivered" delay={200} />
      </div>

      <div data-testid="aging-summary" className="rounded-md border border-slate-200 bg-white p-5">
        <h2 className="text-base font-bold text-slate-900 md:text-lg">Aging Summary — Open Orders</h2>
        <p className="mt-0.5 text-xs text-slate-500">Calculated from order creation date</p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex items-center gap-3 rounded-md border border-[#A7F3D0] bg-[#ECFDF5] p-4">
            <span className="aging-dot green" />
            <div>
              <p className="text-2xl font-bold text-[#047857]" data-testid="aging-green-count">{ac.green}</p>
              <p className="text-xs font-medium text-[#047857]">Fresh (&lt; 3 days)</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-md border border-[#FDE68A] bg-[#FFFBEB] p-4">
            <span className="aging-dot yellow" />
            <div>
              <p className="text-2xl font-bold text-[#B45309]" data-testid="aging-yellow-count">{ac.yellow}</p>
              <p className="text-xs font-medium text-[#B45309]">Nearing delay (3–7 days)</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-md border border-[#FECACA] bg-[#FEF2F2] p-4">
            <span className="aging-dot red" />
            <div>
              <p className="text-2xl font-bold text-[#B91C1C]" data-testid="aging-red-count">{ac.red}</p>
              <p className="text-xs font-medium text-[#B91C1C]">Overdue (&gt; 7 days)</p>
            </div>
          </div>
        </div>
      </div>

      {hasRole(user, "sales", "sales_lead", "admin") && <PendingApprovals />}
      {hasRole(user, "admin") && <InactivityAlerts />}
      {hasRole(user, "admin") && <RecentChanges />}
      {hasRole(user, "admin") && <LowStockAlerts />}
      {hasRole(user, "admin") && <TrendsChart />}
      {hasRole(user, "admin") && <Leaderboard />}

      <div className="rounded-md border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <h2 className="text-base font-bold text-slate-900 md:text-lg">Recent Orders</h2>
          <Link to="/orders" data-testid="view-all-orders-link" className="text-sm font-medium text-slate-600 underline-offset-4 hover:underline">
            View all →
          </Link>
        </div>
        {/* Mobile card list */}
        <div className="divide-y divide-slate-100 md:hidden" data-testid="recent-orders-card-list">
          {stats.recent_orders.map((o) => (
            <Link key={o.id} to={`/orders/${o.id}`} data-testid={`recent-order-card-${o.order_number}`}
              className="block p-4 transition-colors duration-150 active:bg-slate-50">
              <div className="flex items-center justify-between gap-2">
                <p className="font-mono text-sm font-semibold text-slate-900">{o.order_number}</p>
                <span className="font-mono text-xs text-slate-500">Qty {o.total_quantity}</span>
              </div>
              <p className="mt-0.5 truncate text-sm font-medium text-slate-700">{o.customer_name}</p>
              <p className="truncate text-xs text-slate-500">{o.location}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <StatusBadge status={o.status} label={o.status_label} />
                <AgingBadge color={o.aging_color} days={o.age_days} />
              </div>
            </Link>
          ))}
          {stats.recent_orders.length === 0 && (
            <p className="p-6 text-center text-sm text-slate-400">No orders yet.</p>
          )}
        </div>
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">Order #</th>
                <th className="p-3">Customer</th>
                <th className="p-3">Location</th>
                <th className="p-3 text-right">Qty</th>
                <th className="p-3">Status</th>
                <th className="p-3">Aging</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-100 transition-colors duration-150 hover:bg-slate-50">
                  <td className="p-3">
                    <Link to={`/orders/${o.id}`} data-testid={`recent-order-link-${o.order_number}`} className="font-mono font-medium text-slate-900 hover:underline">
                      {o.order_number}
                    </Link>
                  </td>
                  <td className="p-3">{o.customer_name}</td>
                  <td className="p-3 text-slate-500">{o.location}</td>
                  <td className="p-3 text-right font-mono">{o.total_quantity}</td>
                  <td className="p-3"><StatusBadge status={o.status} label={o.status_label} /></td>
                  <td className="p-3"><AgingBadge color={o.aging_color} days={o.age_days} /></td>
                </tr>
              ))}
              {stats.recent_orders.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-slate-400">No orders yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
